"use client";

import { Track } from "@/src/lib/tracking";
import { useCallback } from "react";
import { trackEvent } from "@utils/tracking";
import {
  useResponsiveChatOpen,
  type OpenResponsiveChatParams,
} from "@/src/hooks/useResponsiveChatOpen";
import {
  buildGoalCreatorQuestion,
  buildGoalCreatorWithCategoryQuestion,
  buildPlanActivationQuestion,
  buildGoalReviewQuestion,
  buildPlanNoDraftGoalQuestion,
} from "./buildPlanGoalChatQuestion";
import { persistPlanDraftGoalIdToStorage } from "./planGoalNavigation";
import { resolvePlanResumeDraftMessage } from "./resolvePlanResumeDraftMessage";
import { removeSaveGoalDraftIdFromStorage } from "@hooks/onboarding/saveGoalDraftIdStorage";

/** 内部统一 Chat 打开参数；PC/移动端由 `useResponsiveChatOpen` 分派 */
export type OpenPlanGoalChatParams = OpenResponsiveChatParams;

export type OpenPlanResumeDraftGoalChatParams = {
  /** 草稿 Goal 一句话总结 — 兼容调用签名，不进入消息正文 */
  summary: string;
  /** 草稿展示标题 — 用于操作回显 */
  draftTitle?: string;
  /** 后端注入的草稿 goal_id — 写入 LOCAL_STORAGE_KEYS.GOAL_ID */
  draftGoalId: string;
  /** Explicit diagnosis_content.source; Onboarding passes onboarding_v3_0_1. */
  source?: string;
  /** Raw Goal.diagnosis_content when source is resolved from API entity. */
  diagnosisContent?: string;
};

export type OpenGoalReviewChatParams = {
  goalId: string;
  goalTitle: string;
};

export type OpenPlanActivationGoalChatParams = {
  goalId: string;
  goalTitle: string;
  source: "chat" | "signal" | "plan" | "my_goals";
};

/**
 * Plan Goal 相关 Chat 入口。
 *
 * 打开 Chat 时分端：见 `useResponsiveChatOpen`（大屏跳转 /chat，小屏弹窗）
 *
 * 各场景：
 * 1. `openPlanNoDraftGoalChat` — 无草稿，开 Chat 下发 plan_guide 首条
 * 2. `openPlanResumeDraftGoalChat` — 有草稿，写 GOAL_ID 后开 Chat
 * 3–4. Goal Creator（有/无方向）
 * 5. `openGoalReviewChat` — Goal 详情 Chat with Ed（用户的变化/回顾诉求在此承接）
 */
export function usePlanGoalChatOpen() {
  const { openResponsiveChat } = useResponsiveChatOpen();

  const openPlanGoalChat = useCallback(
    (params: OpenPlanGoalChatParams) => {
      openResponsiveChat(params);
    },
    [openResponsiveChat],
  );

  /** 1. Plan 无草稿 — plan_guide 首条 question，label = 点击卡片文案 */
  const openPlanNoDraftGoalChat = useCallback(
    (dimensionCode: string, label?: string) => {
      const code = dimensionCode.trim();
      removeSaveGoalDraftIdFromStorage();
      if (!code) {
        Track.log("warn", "PlanGoalChat", "plan_guide missing dimensionCode");
        return;
      }

      const question = buildPlanNoDraftGoalQuestion(code, label ?? "");
      Track.log("info", "PlanGoalChat", "open plan no-draft goal chat", {
        dimensionCode: code,
        question,
      });
      openPlanGoalChat({
        question,
        autoRenew: true,
      });
    },
    [openPlanGoalChat],
  );

  /** 2. Plan 有草稿 — LOCAL_STORAGE_KEYS.GOAL_ID + 弹窗续建 */
  const openPlanResumeDraftGoalChat = useCallback(
    ({
      summary,
      draftTitle,
      draftGoalId,
      source,
      diagnosisContent,
    }: OpenPlanResumeDraftGoalChatParams) => {
      persistPlanDraftGoalIdToStorage(draftGoalId);
      const question = resolvePlanResumeDraftMessage({
        draftGoalId,
        draftTitle: draftTitle ?? "",
        summary,
        source,
        diagnosisContent,
      });
      Track.log("info", "PlanGoalChat", "resume draft goal chat", {
        draftGoalId,
        draftTitle,
        summary,
        source,
      });
      openPlanGoalChat({
        question,
        autoRenew: true,
      });
    },
    [openPlanGoalChat],
  );

  /** Existing INACTIVE Goal — refine the persisted goal through plan_activation. */
  const openPlanActivationGoalChat = useCallback(
    ({ goalId, goalTitle, source }: OpenPlanActivationGoalChatParams) => {
      const id = goalId.trim();
      if (!id) {
        Track.log("warn", "PlanGoalChat", "plan activation missing goalId");
        return;
      }
      Track.event("goal_refine_open", { goal_id: id, source });
      openPlanGoalChat({
        question: buildPlanActivationQuestion({
          draftGoalId: id,
          draftTitle: goalTitle,
        }),
        autoRenew: true,
      });
    },
    [openPlanGoalChat],
  );

  /** 3. 有方向（category 走 referenceId；label = 点击卡片文案） */
  const openGoalCreatorWithCategoryChat = useCallback(
    (category: string, entryLabel?: string) => {
      removeSaveGoalDraftIdFromStorage();
      openPlanGoalChat({
        question: buildGoalCreatorWithCategoryQuestion(
          category,
          entryLabel ?? category,
        ),
        autoRenew: true,
      });
    },
    [openPlanGoalChat],
  );

  /** 4. 无方向 */
  const openGoalCreatorChat = useCallback(
    (entryLabel: string) => {
      removeSaveGoalDraftIdFromStorage();
      openPlanGoalChat({
        question: buildGoalCreatorQuestion(entryLabel),
        autoRenew: true,
      });
    },
    [openPlanGoalChat],
  );

  /** 5. Goal 详情 — Chat with Ed（review） */
  const openGoalReviewChat = useCallback(
    ({ goalId, goalTitle }: OpenGoalReviewChatParams) => {
      Track.log("info", "PlanGoalChat", "open goal review chat", {
        goalId,
        goalTitle,
      });
      trackEvent("chat_data_collection_started", {
        goal_id: goalId,
        entry_from: "goal_detail",
      });
      openPlanGoalChat({
        question: buildGoalReviewQuestion({ goalId, goalTitle }),
        autoRenew: true,
      });
    },
    [openPlanGoalChat],
  );

  return {
    openPlanGoalChat,
    openPlanNoDraftGoalChat,
    openPlanResumeDraftGoalChat,
    openPlanActivationGoalChat,
    openGoalCreatorWithCategoryChat,
    openGoalCreatorChat,
    openGoalReviewChat,
  };
}
